import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { StructuredContentDocument } from '@repo/supergraph-schemas/structuredContent/types';
import { BrandIcon, type BrandIconType } from '../../../BrandIcon';
import { Details } from '../../../Details';
import { StructuredContentRenderer } from '../..';

type ExpandCardProps = StructuredContentDocument<{
  icon?: BrandIconType;
  title1?: string;
  title2?: string;
  children: StructuredContentDocument;
}>;

export function ExpandCard({ props: { children, icon, title1, title2 } }: ExpandCardProps) {
  return (
    <Details
      variant="simple"
      detailsProps={{
        sx: {
          borderRadius: '8px',
          minHeight: { xs: 'unset', sm: '152px', md: '177px' },
          padding: 3,
          backgroundColor: 'white',
          justifyContent: 'space-between',
          border: '1px solid',
          borderColor: 'transparent',
          transition: 'border-color 0.3s ease',
          WebkitTapHighlightColor: 'transparent',
          '&:hover': {
            borderColor: 'greens.main',
            cursor: 'pointer',
          },
        },
      }}
      SummaryComponent={
        <Stack gap={{ xs: 3, lg: 3.5 }} sx={{ justifyContent: 'space-between', width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {icon && (
              <BrandIcon
                name={icon}
                size={24}
                svgStyles={{
                  width: {
                    xs: '24px',
                    lg: '32px',
                  },
                  height: {
                    xs: '24px',
                    lg: '32px',
                  },
                  filter:
                    'brightness(0) saturate(100%) invert(36%) sepia(12%) saturate(4565%) hue-rotate(118deg) brightness(97%) contrast(103%)',
                }}
              />
            )}
            <ExpandMoreIcon
              sx={{
                backgroundColor: 'neutral.lightest',
                color: 'neutral.light',
                fontSize: '24px',
                borderRadius: '100%',
                transition: 'transform 0.3s ease',
                justifySelf: 'flex-end',
                '[open] > summary &': {
                  transform: 'rotate(180deg)',
                },
              }}
            />
          </Box>

          <Stack>
            {/* Primary Title */}
            <Typography
              sx={{
                color: 'greens.main',
                fontSize: {
                  xs: '20px',
                  lg: '26px',
                },
                fontWeight: 500,
              }}
            >
              {title1}
            </Typography>
            {/* Secondary Title */}
            <Typography
              sx={{
                color: 'neutral.darker',
                fontWeight: 500,
              }}
            >
              {title2}
            </Typography>
          </Stack>
        </Stack>
      }
    >
      <Box
        sx={{
          marginTop: 2,
          '& p': {
            color: 'neutral.darker',
            fontSize: '13px !important',
            lineHeight: '150%',
          },
        }}
      >
        <StructuredContentRenderer doc={children} />
      </Box>
    </Details>
  );
}
